import { FileType } from '@/api/file';
import { MedusaImport, parseMedusaImportFile as parseMedusaImport, saveMedusaImportRecords as saveMedusaImport } from '@/api/registration';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import { useColumnState } from '@/components/hooks/useColumnState';
import useGridPageSize from '@/components/hooks/useGridPageSize';
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel';
import { DetailGridToolbar } from '@/components/mui-x/datagrid/DetailGridToolbar';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, styled } from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridLogicOperator } from '@mui/x-data-grid';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid-premium/locales';
import { createFileRoute } from '@tanstack/react-router';
import { Buffer } from "buffer";
import { enqueueSnackbar } from 'notistack';
import React from 'react';

export const Route = createFileRoute('/_auth/medusaImport')({
    component: MedusaImportComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function MedusaImportComponent() {

    const [medusaImport, setMedusaImport] = React.useState<MedusaImport[]>([]);
    const [fileContent, setFileContent] = React.useState<string>("");
    const [fileName, setFileName] = React.useState<string>("");

    // const permissionsQuery = useSuspenseQuery(fetchDetailPermissionsQueryOptions('organization', sessionOrganization.uuid, ['RegistrationTransfer']))
    // const permissions = permissionsQuery.data

    // const navigate = useNavigate();
    // const { xsmall, small, medium, large, xlarge } = useResponsive();

    const columns: GridColDef[] = [];
    columns.push(
        {
            field: 'mitid',
            headerName: 'mitid',
            valueGetter: (_value, row) => `${row.medusaRecord.mitid || ''}`,
        },
        {
            field: 'mitlid',
            headerName: 'mitlid',
            valueGetter: (_value, row) => `${row.medusaRecord.mitlid || ''}`,
        },
        {
            field: 'mitsex',
            headerName: 'mitsex',
            valueGetter: (_value, row) => `${row.medusaRecord.mitsex || ''}`,
        },
        {
            field: 'mittitel',
            headerName: 'mittitel',
            valueGetter: (_value, row) => `${row.medusaRecord.mittitel || ''}`,
        },
        {
            field: 'mitvname',
            headerName: 'mitvname',
            valueGetter: (_value, row) => `${row.medusaRecord.mitvname || ''}`,
        },
        {
            field: 'mitnname',
            headerName: 'mitnname',
            valueGetter: (_value, row) => `${row.medusaRecord.mitnname || ''}`,
        },
        {
            field: 'mitstrasse',
            headerName: 'mitstrasse',
            valueGetter: (_value, row) => `${row.medusaRecord.mitstrasse || ''}`,
        },
        {
            field: 'mitplz',
            headerName: 'mitplz',
            valueGetter: (_value, row) => `${row.medusaRecord.mitplz || ''}`,
        },
        {
            field: 'mitort',
            headerName: 'mitort',
            valueGetter: (_value, row) => `${row.medusaRecord.mitort || ''}`,
        },
        {
            field: 'mitlcode',
            headerName: 'mitlcode',
            valueGetter: (_value, row) => `${row.medusaRecord.mitlcode || ''}`,
        },
        {
            field: 'mitgdd',
            headerName: 'mitgdd',
            type: 'number',
            valueGetter: (_value, row) => `${row.medusaRecord.mitgdd || ''}`,
        },
        {
            field: 'mitgmm',
            headerName: 'mitgmm',
            type: 'number',
            valueGetter: (_value, row) => `${row.medusaRecord.mitgmm || ''}`,
        },
        {
            field: 'mitgjjjj',
            headerName: 'mitgjjj',
            type: 'number',
            valueGetter: (_value, row) => `${row.medusaRecord.mitgjjjj || ''}`,
        },
        {
            field: 'mitart',
            headerName: 'mitart',
            valueGetter: (_value, row) => `${row.medusaRecord.mitart || ''}`,
        },
        {
            field: 'mittmx',
            headerName: 'mittmx',
            valueGetter: (_value, row) => `${row.medusaRecord.mittmx || ''}`,
        },
        {
            field: 'mitstatus',
            headerName: 'mitstatus',
            valueGetter: (_value, row) => `${row.medusaRecord.mitstatus || ''}`,
        },
        {
            field: 'mitfunktion',
            headerName: 'mitfunktion',
            valueGetter: (_value, row) => `${row.medusaRecord.mitfunktion || ''}`,
        },
        {
            field: 'mitgebuehr',
            headerName: 'mitgebuehr',
            valueGetter: (_value, row) => `${row.medusaRecord.mitgebuehr || ''}`,
        },
        {
            field: 'mitgebuehrvollzahler',
            headerName: 'mitgebuehrvollzahler',
            valueGetter: (_value, row) => `${row.medusaRecord.mitgebuehrvollzahler || ''}`,
        },
        {
            field: 'mitgkz',
            headerName: 'mitgkz',
            valueGetter: (_value, row) => `${row.medusaRecord.mitgkz || ''}`,
        },
        {
            field: 'mitremark',
            headerName: 'mitremark',
            valueGetter: (_value, row) => `${row.medusaRecord.mitremark || ''}`,
        },
        {
            field: 'attributid1001',
            headerName: 'attributid1001',
            type: 'number',
            valueGetter: (_value, row) => `${row.medusaRecord.attributid1001 || ''}`,
        },
        {
            field: 'attributid1003',
            headerName: 'attributid1003',
            valueGetter: (_value, row) => `${row.medusaRecord.attributid1003 || ''}`,
        },
        {
            field: 'attributid1004',
            headerName: 'attributid1004',
            valueGetter: (_value, row) => `${row.medusaRecord.attributid1004 || ''}`,
        },
        {
            field: 'attributid1005',
            headerName: 'attributid1005',
            valueGetter: (_value, row) => `${row.medusaRecord.attributid1005 || ''}`,
        },
        {
            field: 'attributid1006',
            headerName: 'attributid1006',
            valueGetter: (_value, row) => `${row.medusaRecord.attributid1006 || ''}`,
        }
    )

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('medusaImport', {})
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const defaultLocaleText = deDE.components.MuiDataGrid.defaultProps.localeText
    const localeText = { ...defaultLocaleText, ...{ toolbarExportLabel: 'Export' } }

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('medusaImport', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('medusaImport', 35));

    const handleFileUpload = (files: FileList) => {
        const file = files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = Buffer.from(reader.result as string).toString('base64')
            const uploadFile = { filename: file.name, base64: base64 } as FileType;
            parseMedusaImport(uploadFile).then(data => {
                setMedusaImport(data.data);
            }).catch(error => {
                console.error("Error parsing Medusa import file:", error);
            });
            setFileName(file.name);
            setFileContent(reader.result as string);
        }
        reader.readAsText(file, 'windows-1252')
    }

    const handleSave = () => {
        console.log("Saving Medusa import results ...")
        if (medusaImport.length > 0) {
            const base64 = Buffer.from(fileContent).toString('base64')
            const uploadFile = { filename: fileName, base64: base64 } as FileType;
            saveMedusaImport(uploadFile).then(() => {
                setMedusaImport([]);
                enqueueSnackbar('Import wurde gestartet, Protokoll wird per Mail verschickt', { variant: 'success' });
            })
        }
    }

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    const CustomToolbar = () => {
        return (
            <DetailGridToolbar scope={'medusaImport'} getScopeName={() => 'medusaImport'} addLeft={
                <>
                    <Button
                        component="label"
                        role={undefined}
                        tabIndex={-1}
                        startIcon={<UploadIcon />}
                    >
                        Datei auswählen
                        <VisuallyHiddenInput
                            type="file"
                            accept=".csv,.asc"
                            onChange={(event) => { if (event.target.files) { handleFileUpload(event.target.files) } }}
                        />
                    </Button>
                    <Button
                        component="label"
                        role={undefined}
                        tabIndex={-1}
                        startIcon={<SaveIcon />}
                        onClick={() => { handleSave() }}
                        disabled={medusaImport.length === 0}
                    >
                        Ergebnisse speichern
                    </Button>
                </>
            } />
        )
    }

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('medusaImport', apiRef, columns);

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DataGridPremium
                    columns={columnsState.columns}
                    onColumnWidthChange={columnsState.onColumnWidthChange}
                    onColumnOrderChange={columnsState.onColumnOrderChange}
                    rows={medusaImport}
                    getRowId={(row) => { return row.uuid || '' }}
                    localeText={localeText}
                    pagination
                    pageSizeOptions={pageSizeOptions}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model)
                        gridPageSize.handlePaginationModelChange('awards', model)
                    }}
                    slots={{ toolbar: CustomToolbar }}
                    slotProps={{
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                            sx: { maxWidth: '100vw' }
                        },
                    }}
                    density='compact'
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={handleVisibilityModelChange}
                    hideFooter={true}
                    disableDensitySelector
                    disableAggregation
                    disableColumnPinning
                    disableColumnReorder
                    disableRowGrouping
                    disablePivoting
                    showToolbar />
            </div>
        </Box>
    );
}
